import type { FeedbackRun, ProgressReport } from './report';

export type FlowBlock = 'banner' | 'nothing' | 'history' | 'skills' | 'finding' | 'access' | 'writing' | 'speaking' | 'samples' | 'nextSteps' | 'feedback' | `feedback:${string}`;

export type SectionId = 'history' | 'skills' | 'finding' | 'access' | 'writing' | 'speaking' | 'nextSteps' | 'feedback';

export const REPORT_SECTIONS: readonly { id: SectionId; label: string }[] = [
  { id: 'history', label: 'Benchmark growth history' },
  { id: 'skills', label: 'Indicator scores' },
  { id: 'finding', label: 'Summary' },
  { id: 'access', label: 'ACCESS test results' },
  { id: 'writing', label: 'Writing sample' },
  { id: 'speaking', label: 'Speaking sample' },
  { id: 'nextSteps', label: 'Next steps' },
  { id: 'feedback', label: 'Benchmark feedback' },
];

export function sectionOf(kind: FlowBlock): string | null {
  const head = kind.split(':')[0]!;
  return head === 'banner' || head === 'nothing' ? null : head;
}

function listed(section: string): section is SectionId {
  return REPORT_SECTIONS.some((entry) => entry.id === section);
}

export function chosenBlocks(blocks: FlowBlock[], chosen: ReadonlySet<SectionId>): FlowBlock[] {
  return blocks.flatMap((kind): FlowBlock[] => {
    if (kind === 'banner') return [kind];
    if (kind === 'nothing') return [];
    if (kind === 'samples') {
      const writing = chosen.has('writing');
      const speaking = chosen.has('speaking');
      if (writing && speaking) return ['samples'];
      if (writing) return ['writing'];
      if (speaking) return ['speaking'];
      return [];
    }
    const section = sectionOf(kind);
    if (section === null || !listed(section)) return [kind];
    return chosen.has(section) ? [kind] : [];
  });
}

export function printsNothing(blocks: FlowBlock[], chosen: ReadonlySet<SectionId>): boolean {
  return chosenBlocks(blocks, chosen).every((kind) => kind === 'banner');
}

export function hasSamples(report: ProgressReport): boolean {
  return report.samples.writing.length > 0 || report.samples.speaking.length > 0;
}

// The sections of a report in reading order, leaving out any with nothing to
// show. One writing sample and one speaking sample are each a half-width
// column, so they share a block side by side.
export function flowBlocks(report: ProgressReport): FlowBlock[] {
  const { benchmarks, access, samples } = report;
  const blocks: FlowBlock[] = ['banner'];
  if (benchmarks.shown.length === 0 && access === null && !hasSamples(report)) blocks.push('nothing');
  if (benchmarks.history.length >= 2) blocks.push('history');
  if (benchmarks.shown.length > 0) blocks.push('skills');
  if (benchmarks.narrative.length > 0) blocks.push('finding');
  if (access !== null) blocks.push('access');
  if (samples.writing.length === 1 && samples.speaking.length === 1) {
    blocks.push('samples');
  } else {
    if (samples.writing.length > 0) blocks.push('writing');
    if (samples.speaking.length > 0) blocks.push('speaking');
  }
  if (report.nextSteps) blocks.push('nextSteps');
  if (report.feedback) blocks.push('feedback');
  return blocks;
}

// Fills each page in order. A block never splits: one that does not fit on the
// current page moves whole to the next.
export function packPages(heights: number[], capacity: number): number[][] {
  const pages: number[][] = [[]];
  let used = 0;
  heights.forEach((height, i) => {
    const page = pages[pages.length - 1]!;
    if (page.length > 0 && used + height > capacity) {
      pages.push([i]);
      used = height;
    } else {
      page.push(i);
      used += height;
    }
  });
  return pages;
}

export type FeedbackPart = {
  key: `feedback:${string}`;
  runs: FeedbackRun[];
  opensSection: boolean;
  opensParagraph: boolean;
  closesSection: boolean;
  // First on a page partway through the section: shows "… benchmark · continued".
  continued: boolean;
  // Last on a page when the section goes on: shows "Continued on page N →".
  continuesTo: number | null;
};

// Space above a piece of feedback inside its section: the title opens the
// section, and paragraphs are 12px apart. pages.tsx draws these.
export const FEEDBACK_TOP = { section: 16, paragraph: 12 } as const;

export function feedbackParts(report: ProgressReport): FeedbackPart[] {
  const paragraphs = report.feedback?.paragraphs ?? [];
  return paragraphs.map((runs, i) => ({
    key: `feedback:${i}` as const,
    runs,
    opensSection: i === 0,
    opensParagraph: true,
    closesSection: i === paragraphs.length - 1,
    continued: false,
    continuesTo: null,
  }));
}

// One printed line of a measured paragraph: the number of words up to the end
// of the line, and the line's bottom edge in px from the top of its block.
export type Line = { words: number; bottom: number };

export type Measured = { kind: FlowBlock; height: number; part?: FeedbackPart; lines?: Line[] };

export type Placed = { kind: FlowBlock; part?: FeedbackPart };

// Fewest lines a break may leave on either side of it.
const MIN_LINES = 2;

function topOf(part: FeedbackPart): number {
  if (part.opensSection) return FEEDBACK_TOP.section;
  return part.opensParagraph ? FEEDBACK_TOP.paragraph : 0;
}

// Fills each page in order. A section that does not fit moves whole to the
// next page, except the feedback: it is text, so it fills the rest of the page
// and continues on the next, breaking between lines. A break leaves at least
// two lines on each side and never strands the title. The page before a break
// ends "Continued on page N", and the page after starts "… · continued";
// `extra` is the measured height each of those labels adds.
export function layOut(items: Measured[], capacity: number, extra: { more: number; continued: number }): Placed[][] {
  const pages: Placed[][] = [[]];
  let used = 0;
  const current = () => pages[pages.length - 1]!;
  const newPage = () => {
    pages.push([]);
    used = 0;
  };

  items.forEach((item, index) => {
    if (!item.part || !item.lines) {
      if (current().length > 0 && used + item.height > capacity) newPage();
      current().push({ kind: item.kind });
      used += item.height;
      return;
    }
    const rest = items.slice(index + 1).reduce((sum, next) => sum + (next.part ? next.height : 0), 0);
    let part = item.part;
    let lines = item.lines;
    let height = item.height;
    for (;;) {
      const continued = current().length === 0 && !part.opensSection;
      const own = continued ? height - topOf(part) + extra.continued : height;
      const end = used + own;
      if (end <= capacity && (part.closesSection || end + rest <= capacity || end + extra.more <= capacity)) {
        current().push({ kind: part.key, part: { ...part, continued } });
        used = end;
        return;
      }
      const room = capacity - used - extra.more - (continued ? extra.continued - topOf(part) : 0);
      let cut = -1;
      lines.forEach((line, i) => {
        if (line.bottom <= room && i + 1 >= MIN_LINES && lines.length - (i + 1) >= MIN_LINES) cut = i;
      });
      if (cut >= 0) {
        const at = lines[cut]!;
        const [head, tail] = splitRuns(part.runs, at.words);
        current().push({
          kind: `${part.key}/a`,
          part: { ...part, key: `${part.key}/a`, runs: head, closesSection: false, continued, continuesTo: pages.length + 1 },
        });
        newPage();
        part = { ...part, key: `${part.key}/b`, runs: tail, opensSection: false, opensParagraph: false };
        lines = lines.slice(cut + 1).map((line) => ({ words: line.words - at.words, bottom: line.bottom - at.bottom }));
        height -= at.bottom;
        continue;
      }
      if (current().length === 0) {
        current().push({ kind: part.key, part: { ...part, continued } });
        used = own;
        return;
      }
      const last = current()[current().length - 1]!;
      if (last.part) last.part = { ...last.part, continuesTo: pages.length + 1 };
      newPage();
    }
  });
  return pages;
}

// Cuts formatted text after `count` words, keeping each run's marks. The space
// at the cut is dropped from both sides.
export function splitRuns(runs: FeedbackRun[], count: number): [FeedbackRun[], FeedbackRun[]] {
  const head: FeedbackRun[] = [];
  const tail: FeedbackRun[] = [];
  let seen = 0;
  runs.forEach((run) => {
    if (seen >= count) {
      tail.push(run);
      return;
    }
    const pattern = /\S+/g;
    let match: RegExpExecArray | null;
    let cutAt = -1;
    while ((match = pattern.exec(run.text)) !== null) {
      seen += 1;
      if (seen === count) {
        cutAt = match.index + match[0].length;
        break;
      }
    }
    if (cutAt < 0) {
      head.push(run);
      return;
    }
    head.push({ ...run, text: run.text.slice(0, cutAt) });
    const after = run.text.slice(cutAt).trimStart();
    if (after) tail.push({ ...run, text: after });
  });
  const lastHead = head[head.length - 1];
  if (lastHead) lastHead.text = lastHead.text.trimEnd();
  if (tail[0]) tail[0] = { ...tail[0], text: tail[0].text.trimStart() };
  return [head.filter((run) => run.text), tail.filter((run) => run.text)];
}
